/**
 * AuthContext - Provides global authentication state management
 * Wraps useAuth hook in a context for component access
 */

import { createContext, useContext, ReactNode } from 'react';
import { useAuth, UseAuthReturn } from '../hooks/useAuth';

/**
 * Auth context type
 */
type AuthContextType = UseAuthReturn;

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props for AuthProvider
 */
interface AuthProviderProps {
  /** Child components */
  children: ReactNode;
}

/**
 * AuthProvider component
 * Provides authentication state and actions to all child components
 * 
 * @param props - Provider props
 * @returns Provider component
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const authState = useAuth();

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 * 
 * @returns Authentication state and actions
 * @throws Error if used outside AuthProvider
 */
export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  
  return context;
}
